import { useRef, useEffect, useState, ReactNode } from 'react';
import { SelectedIndexData } from '../../../../types';

function findStepElement(container: HTMLElement, stepIndex: number): HTMLElement | null {
  const stepSections = container.querySelectorAll('.step-section');

  for (const element of Array.from(stepSections)) {
    const attr = (element as HTMLElement).getAttribute('data-step-index');
    if (attr !== null && parseInt(attr, 10) === stepIndex) {
      return element as HTMLElement;
    }
  }

  return (stepSections[stepIndex] as HTMLElement) || null;
}

/** Smooth-scrolls the container back to `top`. */
function scrollBackTo(element: HTMLElement, top: number) {
  element.scroll({ top, behavior: 'smooth' });
}

interface UseScrollManagementParams {
  selectedIndexData: SelectedIndexData;
  setSelectedIndexData: (data: SelectedIndexData) => void;
  currentStep: number;
  maxAllowedStepIndex?: number;
  isStepCompleted?: (flowIndex: number) => boolean;
  children: ReactNode;
}

export function useScrollManagement({
  selectedIndexData,
  setSelectedIndexData,
  currentStep,
  maxAllowedStepIndex,
  isStepCompleted,
  children,
}: UseScrollManagementParams) {
  const componentRef = useRef<HTMLDivElement>(null);
  const selectedIndex = +selectedIndexData.value;

  const maxAllowedStepIndexRef = useRef<number | undefined>(maxAllowedStepIndex);
  const currentStepRef = useRef<number>(currentStep);
  const isBlockingScrollRef = useRef<boolean>(false);
  // True while a finger is on the screen: iOS ignores/fights programmatic scrolls and
  // non-cancelable touchmoves mid-gesture, so corrections wait until the gesture ends.
  const isTouchingRef = useRef<boolean>(false);

  const [nextSelectedStep, setNextSelectedStep] = useState(0);

  useEffect(() => { maxAllowedStepIndexRef.current = maxAllowedStepIndex; }, [maxAllowedStepIndex]);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);

  const selectedIndexRef = useRef<number>(selectedIndex);
  const isStepCompletedRef = useRef(isStepCompleted);
  useEffect(() => { selectedIndexRef.current = selectedIndex; }, [selectedIndex]);
  useEffect(() => { isStepCompletedRef.current = isStepCompleted; }, [isStepCompleted]);

  // Disable pointer events on steps beyond the max clickable step
  useEffect(() => {
    if (!componentRef.current) return;

    const stepSections = componentRef.current.querySelectorAll('.step-section');
    const maxClickable = maxAllowedStepIndex !== undefined ? maxAllowedStepIndex + 1 : currentStep;

    stepSections.forEach((el) => {
      const attr = (el as HTMLElement).getAttribute('data-step-index');
      if (attr !== null) {
        const idx = parseInt(attr, 10);
        (el as HTMLElement).style.pointerEvents = idx > maxClickable ? 'none' : 'auto';
      }
    });
  }, [currentStep, maxAllowedStepIndex, children]);

  // IntersectionObserver: detect which step is most visible during scroll
  useEffect(() => {
    if (!componentRef.current) return;

    const targetElements = componentRef.current.querySelectorAll('.step-section');

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      if (isBlockingScrollRef.current) return;

      let mostVisibleEntry: IntersectionObserverEntry | null = null;
      let highestRatio = 0;

      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
          highestRatio = entry.intersectionRatio;
          mostVisibleEntry = entry;
        }
      }

      if (!mostVisibleEntry) return;

      const targetElement = mostVisibleEntry.target as HTMLElement;
      const stepIndexAttr = targetElement.getAttribute('data-step-index');
      const inScreenIndex = stepIndexAttr !== null
        ? parseInt(stepIndexAttr, 10)
        : Array.from(targetElements).findIndex(e => e === targetElement);

      if (isNaN(inScreenIndex) || inScreenIndex < 0) return;
      if (isBlockingScrollRef.current) return;
      if (
        maxAllowedStepIndexRef.current === undefined &&
        inScreenIndex > currentStepRef.current &&
        currentStepRef.current < 7
      ) return;

      setNextSelectedStep(inScreenIndex);
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: componentRef.current,
      threshold: [0.3, 0.5, 0.8],
    });

    targetElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [children, maxAllowedStepIndex]);

  // Mark scroll origin when user physically scrolls (wheel/touch)
  useEffect(() => {
    const element = componentRef.current;
    if (!element) return;

    const markScrollOrigin = () => {
      if (selectedIndexData.origin !== 'scroll') {
        setSelectedIndexData({ value: selectedIndexData.value, origin: 'scroll' });
      }
    };

    element.addEventListener('wheel', markScrollOrigin, { passive: true });
    element.addEventListener('touchstart', markScrollOrigin, { passive: true });

    return () => {
      element.removeEventListener('wheel', markScrollOrigin);
      element.removeEventListener('touchstart', markScrollOrigin);
    };
  }, [selectedIndexData, setSelectedIndexData, maxAllowedStepIndex, isStepCompleted]);

  // Forward scroll guard (desktop + mobile, both flows).
  // The user can scroll freely up to the active step (and within it, if it is taller
  // than the viewport) but not past it:
  // - Desktop wheel: clamped before it scrolls.
  // - Touch: the swipe is never cancelled mid-gesture (iOS can't do it reliably and it
  //   leaves the page stuck). When the finger is lifted and momentum settles, the page
  //   smoothly scrolls back to the active step, like desktop.
  // - Scrollbar drag / keyboard: smoothly scrolled back once scrolling settles.
  // No limit once the resume step is the active one, so the final CTA stays reachable.
  useEffect(() => {
    const element = componentRef.current;
    if (!element) return;

    // Non-logged-in -> current (first unanswered) step until step 7.
    // Logged-in -> selected step while incomplete, otherwise maxAllowed + 1.
    const getLimitStep = (): HTMLElement | null => {
      const maxAllowed = maxAllowedStepIndexRef.current;
      let limitStepIndex: number;

      if (maxAllowed === undefined) {
        if (currentStepRef.current >= 7) return null;
        limitStepIndex = currentStepRef.current;
      } else {
        const selected = selectedIndexRef.current;
        const isCompleted = isStepCompletedRef.current ? isStepCompletedRef.current(selected) : true;
        limitStepIndex = isCompleted ? maxAllowed + 1 : selected;
      }

      const stepElement = findStepElement(element, limitStepIndex);
      if (!stepElement || stepElement.classList.contains('resume')) return null;
      return stepElement;
    };

    // Max scrollTop: the active step's top, or its bottom aligned with the viewport
    // bottom when the step is taller than the viewport.
    const getMaxScrollTop = (): number | null => {
      const limitStep = getLimitStep();
      if (!limitStep) return null;
      return limitStep.offsetTop + Math.max(0, limitStep.offsetHeight - element.clientHeight);
    };

    const onWheel = (event: WheelEvent) => {
      if (event.deltaY <= 0) return;
      const maxScrollTop = getMaxScrollTop();
      if (maxScrollTop === null || element.scrollTop + event.deltaY <= maxScrollTop) return;
      if (event.cancelable) event.preventDefault();
      if (element.scrollTop < maxScrollTop) element.scrollTop = maxScrollTop;
    };

    // Smooth return to the limit once scrolling has settled (no scroll events for
    // SETTLE_MS) and no finger is on the screen.
    const SETTLE_MS = 140;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const returnIfOvershot = () => {
      if (isTouchingRef.current) return;
      const maxScrollTop = getMaxScrollTop();
      if (maxScrollTop === null || element.scrollTop <= maxScrollTop + 4) return;
      scrollBackTo(element, maxScrollTop);
    };

    const scheduleReturn = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(returnIfOvershot, SETTLE_MS);
    };

    const onTouchStart = () => {
      isTouchingRef.current = true;
      clearTimeout(settleTimer);
    };

    const onTouchEnd = () => {
      isTouchingRef.current = false;
      scheduleReturn(); // if momentum follows, scroll events keep postponing it
    };

    const onScroll = () => {
      if (isTouchingRef.current) return;
      scheduleReturn();
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchend', onTouchEnd, { passive: true });
    element.addEventListener('touchcancel', onTouchEnd, { passive: true });
    element.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      clearTimeout(settleTimer);
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', onTouchEnd);
      element.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Scroll behavior: handle step changes detected by IntersectionObserver
  useEffect(() => {
    if (selectedIndexData.origin !== 'scroll') return;
    if (nextSelectedStep < 0 || isNaN(nextSelectedStep)) return;

    // Logged-in user flow: enforce step boundaries
    if (maxAllowedStepIndex !== undefined) {
      const currentStepCompleted = isStepCompleted ? isStepCompleted(selectedIndex) : true;
      let targetStepIndex: number | undefined = undefined;

      if (nextSelectedStep > maxAllowedStepIndex + 1) {
        targetStepIndex = maxAllowedStepIndex;
      } else if (!currentStepCompleted && nextSelectedStep > selectedIndex) {
        targetStepIndex = selectedIndex;
      }

      if (targetStepIndex !== undefined) {
        setSelectedIndexData({ value: targetStepIndex, origin: 'scroll' });

        requestAnimationFrame(() => {
          if (!componentRef.current) return;
          const targetElement = findStepElement(componentRef.current, targetStepIndex!);
          if (targetElement && !isTouchingRef.current) {
            scrollBackTo(componentRef.current, targetElement.offsetTop);
          }
        });
        return;
      }
    }

    // Non-logged-in flow: block forward scrolling beyond current step
    if (
      maxAllowedStepIndex === undefined &&
      ((nextSelectedStep > currentStep && currentStep < 7) || (nextSelectedStep === 0 && selectedIndex === 10))
    ) {
      isBlockingScrollRef.current = true;
      const targetStepForScroll = selectedIndex;
      setNextSelectedStep(targetStepForScroll);

      if (nextSelectedStep !== targetStepForScroll) {
        setSelectedIndexData({ value: targetStepForScroll, origin: 'scroll' });
      }

      requestAnimationFrame(() => {
        if (!componentRef.current) {
          isBlockingScrollRef.current = false;
          return;
        }

        const targetElement = findStepElement(componentRef.current, targetStepForScroll);
        if (targetElement) {
          const scrollTop = targetElement.offsetTop;
          componentRef.current.scrollTop = scrollTop;

          requestAnimationFrame(() => {
            if (componentRef.current) {
              componentRef.current.scroll({ top: scrollTop, behavior: 'smooth' });
            }
          });

          setTimeout(() => {
            isBlockingScrollRef.current = false;
          }, 700);
        } else {
          isBlockingScrollRef.current = false;
        }
      });
      return;
    }

    // Normal update: sync selected index with detected step
    if (nextSelectedStep !== selectedIndex) {
      setSelectedIndexData({ value: nextSelectedStep, origin: 'scroll' });
    }
  }, [nextSelectedStep, currentStep, selectedIndexData.origin, selectedIndex, setSelectedIndexData, maxAllowedStepIndex]);

  // Button click behavior: scroll to selected step when triggered by non-scroll action
  useEffect(() => {
    if (!componentRef.current) return;
    if (selectedIndexData.origin === 'scroll') return;

    const targetElement = findStepElement(componentRef.current, selectedIndexData.value);
    if (!targetElement) return;

    componentRef.current.scroll({ top: targetElement.offsetTop, behavior: 'smooth' });
  }, [componentRef.current, selectedIndexData]);

  return { componentRef };
}
